// DNA CONNECT - Carteirinha Digital (Página Dedicada)
// Design 100% idêntico ao modelo oficial
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function DnaConnectCarteirinha() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [aluno, setAluno] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const validadeDate = `31/12/${currentYear}`;
  
  // Dados da escola
  const escola = {
    nome: "NOVO DNA",
    subtitulo: "Muitas mentes, uma missão!",
    slogan: "Muitas mentes, uma missão!",
    hashtag: "#muitasmentesumamissão",
    endereco: "Estrada Benedito Cesario de Oliveira, 156/202 - Vila Iasi -",
    cidade: "Taboão da Serra - SP",
    telefones: "11 5194-4993 / 11 97479-5016",
    website: "novodna.com.br",
    instagram: "@novodna.instituto",
    logo: "/novo-dna-logo.png"
  };

  useEffect(() => {
    loadAluno();
  }, [studentId]);

  const loadAluno = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/filhos/${studentId}?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setAluno(data);
      }
    } catch (error) {
      console.error('Erro ao carregar aluno:', error);
      toast.error('Erro ao carregar dados do aluno');
    } finally {
      setLoading(false);
    }
  };

  // Extrair turma curta
  const getTurmaShort = () => {
    if (!aluno?.class_name) return '-';
    const parts = aluno.class_name.split(' - ');
    return parts[0] ? `${parts[0]} - ...` : aluno.class_name;
  };

  // Determinar segmento
  const getSegmento = () => {
    if (!aluno?.class_name) return '-';
    if (aluno.class_name.includes('E.M') || aluno.class_name.includes('Médio')) return 'Ensino Médio';
    if (aluno.class_name.includes('E.F2') || aluno.class_name.includes('6º') || aluno.class_name.includes('7º') || aluno.class_name.includes('8º') || aluno.class_name.includes('9º')) return 'Fundamental II';
    if (aluno.class_name.includes('E.F1') || aluno.class_name.includes('E.F')) return 'Fundamental I';
    return 'Ed. Infantil';
  };

  // Truncar nome se muito longo
  const getTruncatedName = (maxLength = 25) => {
    if (!aluno?.name) return '-';
    if (aluno.name.length <= maxLength) return aluno.name;
    return aluno.name.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!aluno) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-600 mb-4">Aluno não encontrado</p>
        <Button onClick={() => navigate('/familia/filhos')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-green-50 py-6 px-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/familia/filhos')}
          className="mb-4 text-purple-600 hover:text-purple-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para Meus Filhos
        </Button>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-purple-600">Carteirinha Digital</h1>
        </div>
      </div>

      {/* Container das Carteirinhas */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
          
          {/* ========== FRENTE ========== */}
          <div className="space-y-4">
            <p className="text-center text-gray-400 font-medium tracking-[0.4em] text-sm">FRENTE</p>
            
            {/* Card com borda gradiente */}
            <div className="relative">
              {/* Borda gradiente externa */}
              <div className="absolute -inset-1 bg-gradient-to-br from-purple-400 via-pink-300 to-green-400 rounded-3xl opacity-70"></div>
              
              {/* Card branco */}
              <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Header com Logo */}
                <div className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src={escola.logo} 
                      alt="Logo NOVO DNA" 
                      className="w-16 h-16 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div>
                      <p className="font-bold text-purple-700 text-xl">{escola.nome}</p>
                      <p className="text-gray-500 text-sm tracking-wide">{escola.subtitulo}</p>
                    </div>
                  </div>
                  <div className="bg-purple-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold tracking-wider shadow-md">
                    CARTEIRA DIGITAL
                  </div>
                </div>

                {/* Conteúdo Principal */}
                <div className="px-6 pb-6">
                  <div className="flex gap-6">
                    {/* Foto */}
                    <div className="flex-shrink-0">
                      <div className="w-[160px] h-[200px] rounded-xl overflow-hidden border-4 border-purple-300 shadow-lg bg-gray-100">
                        <img 
                          src={aluno.photo_url || "/default-avatar.png"} 
                          onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }}
                          alt={aluno.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Dados */}
                    <div className="flex-1 flex flex-col justify-between py-1">
                      {/* Nome */}
                      <div>
                        <p className="text-sm text-gray-400 uppercase tracking-wider mb-1">Nome do Aluno</p>
                        <p className="font-bold text-gray-800 text-2xl leading-tight">{aluno.name}</p>
                      </div>

                      {/* RM e RA */}
                      <div className="flex gap-10 mt-4">
                        <div>
                          <span className="text-sm text-gray-400">R.M:</span>
                          <span className="font-bold text-amber-500 text-2xl ml-2">{aluno.registration_number || '-'}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-400">R.A:</span>
                          <span className="font-bold text-purple-600 text-xl ml-2">{aluno.ra || '-'}</span>
                        </div>
                      </div>

                      {/* Turma, Segmento e QR */}
                      <div className="flex items-end gap-6 mt-4">
                        <div>
                          <p className="text-sm text-gray-400 uppercase">Turma</p>
                          <p className="font-semibold text-gray-700 text-lg">{getTurmaShort()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400 uppercase">Segmento</p>
                          <p className="font-semibold text-gray-700 text-lg">{getSegmento()}</p>
                        </div>
                        <div className="ml-auto">
                          <QRCodeSVG 
                            value={`DNASTUDENT:${aluno.registration_number || aluno.id}`}
                            size={80}
                            level="M"
                          />
                        </div>
                      </div>

                      {/* Ano Letivo, Validade e Website */}
                      <div className="flex items-end justify-between mt-4">
                        <div className="flex gap-8">
                          <div>
                            <p className="text-sm text-gray-400 uppercase">Ano Letivo</p>
                            <p className="font-bold text-green-500 text-3xl">{currentYear}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400 uppercase">Validade</p>
                            <p className="font-bold text-gray-700 text-xl">{validadeDate}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                          <span>🌐</span>
                          <span>{escola.website}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer - Slogan */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                  <p className="text-center text-gray-500 text-base italic">"{escola.slogan}"</p>
                </div>
              </div>
            </div>
          </div>

          {/* ========== VERSO ========== */}
          <div className="space-y-4">
            <p className="text-center text-gray-400 font-medium tracking-[0.4em] text-sm">VERSO</p>
            
            {/* Card com borda gradiente */}
            <div className="relative">
              {/* Borda gradiente externa - lado direito mais verde */}
              <div className="absolute -inset-1 bg-gradient-to-br from-purple-400 via-blue-300 to-green-400 rounded-3xl opacity-70"></div>
              
              {/* Card branco */}
              <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col" style={{minHeight: '480px'}}>
                {/* Header com Logo */}
                <div className="p-5 flex items-center gap-4">
                  <img 
                    src={escola.logo} 
                    alt="Logo NOVO DNA" 
                    className="w-16 h-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <p className="font-bold text-gray-800 text-lg">{escola.nome} {escola.subtitulo}</p>
                    <p className="text-pink-500 font-semibold text-lg">{escola.hashtag}</p>
                  </div>
                </div>

                {/* Conteúdo */}
                <div className="px-6 flex-1">
                  {/* Documento Estudantil + QR Instagram */}
                  <div className="flex gap-6">
                    <div className="flex-1">
                      {/* Título */}
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-amber-500 text-xl">📋</span>
                        <p className="font-bold text-red-600 uppercase tracking-wide">Documento Estudantil - Meia-Entrada</p>
                      </div>
                      
                      {/* Texto */}
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Este documento garante ao estudante o direito à <span className="font-semibold text-pink-600">meia-entrada</span> em eventos 
                        artísticos, culturais, esportivos e de lazer, conforme a <span className="font-semibold">Lei Federal nº 
                        12.933/2013</span> regulamentada pelo <span className="font-semibold">Decreto nº 8.537/2015</span>. O benefício é 
                        válido em todo território nacional e deve ser apresentado junto a um 
                        documento de identificação com foto.
                      </p>
                    </div>
                    
                    {/* QR Code Instagram */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="p-1.5 rounded-xl bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600">
                        <div className="bg-white p-2 rounded-lg">
                          <QRCodeSVG 
                            value={`https://instagram.com/novodna.instituto`}
                            size={110}
                            level="M"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mt-3">
                        <span className="text-pink-500 text-lg">📷</span>
                        <span className="text-gray-700 font-semibold">{escola.instagram}</span>
                      </div>
                      <p className="text-gray-400 text-sm italic">Siga-nos nas redes</p>
                    </div>
                  </div>

                  {/* Dados da Escola */}
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <p className="font-bold text-gray-400 uppercase tracking-wider mb-4">Dados da Escola</p>
                    
                    <div className="space-y-2">
                      <div className="flex">
                        <span className="text-gray-400 w-24 flex-shrink-0">Endereço:</span>
                        <span className="text-gray-600">{escola.endereco}<br/>{escola.cidade}</span>
                      </div>
                      <div className="flex">
                        <span className="text-gray-400 w-24 flex-shrink-0">Telefone:</span>
                        <span className="text-purple-600 font-medium">{escola.telefones}</span>
                      </div>
                      <div className="flex">
                        <span className="text-gray-400 w-24 flex-shrink-0">Website:</span>
                        <span className="text-purple-600 font-medium">{escola.website}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Gradiente */}
                <div className="bg-gradient-to-r from-pink-500 via-purple-400 to-green-500 px-6 py-4 flex justify-between items-center mt-auto">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-white" />
                    <span className="text-white font-semibold text-lg">Carteirinha Digital - Apresente pelo celular</span>
                  </div>
                  <span className="text-white font-semibold">{currentYear} © Novo DNA Instituto</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dica */}
        <div className="bg-purple-50 rounded-xl p-6 text-center mt-10">
          <p className="text-purple-700 text-lg">
            <span className="text-2xl mr-2">💡</span>
            <strong>Dica:</strong> Tire um print desta tela ou salve no seu celular para usar como carteirinha digital em cinemas, teatros e eventos culturais.
          </p>
        </div>
      </div>
    </div>
  );
}
